import { Progress, Modal, Button } from 'antd'
import React, { useEffect, useState } from 'react'
import './style.less'
import { SyncOutlined } from '@ant-design/icons'

declare const ProgressStatuses: ['normal', 'exception', 'active', 'success']

export default function LockProgress({
    exportFunction,
    requestLock,
    setIsLoading,
    onFail,
}: {
    exportFunction: (callback: any) => Promise<void>
    requestLock: boolean
    setIsLoading: (isDone: boolean) => void
    onFail: any
}) {
    const [visible, setVisible] = useState(false)
    const [loading, setLoading] = useState(false)
    const [overallProgress, setOverallProgress] = useState(0)
    const [overallStatus, setOverallStatus] = useState<typeof ProgressStatuses[number]>('normal')
    const [initProgress, setInitProgress] = useState(0)
    const [initstoreStatus, setInitStatus] = useState<typeof ProgressStatuses[number]>('active')
    const [collectProgress, setCollectProgress] = useState(0)
    const [collectStatus, setCollectStatus] = useState<typeof ProgressStatuses[number]>('normal')
    const [buildProgress, setBuildProgress] = useState(0)
    const [buildStatus, setBuildStatus] = useState<typeof ProgressStatuses[number]>('normal')
    const [storeProgress, setStoreProgress] = useState(0)
    const [storeStatus, setStoreStatus] = useState<typeof ProgressStatuses[number]>('active')
    const [isDone, setIsDone] = useState(false)

    function exportCallback(meta) {
        if (meta.type === 'server_side') {
            setOverallProgress(meta.meta.percent)
            setOverallStatus(meta.status)
        } else if (meta.type === 'initializing') {
            setInitProgress(meta.meta.percent)
            setInitStatus(meta.status)
        } else if (meta.type === 'fetchingData') {
            setCollectProgress(meta.meta.percent)
            setCollectStatus(meta.status)
        } else if (meta.type === 'compiling') {
            setBuildProgress(meta.meta.percent)
            setBuildStatus(meta.status)
        } else if (meta.type === 'submitData') {
            setStoreProgress(meta.meta.percent)
            setStoreStatus(meta.status)
        }
    }

    function close() {
        setVisible(false)
        setLoading(false)
        setInitProgress(0)
        setInitStatus('active')
        setCollectProgress(0)
        setCollectStatus('normal')
        setBuildProgress(0)
        setBuildStatus('normal')
        setStoreProgress(0)
        setStoreStatus('normal')
        setIsDone(false)
    }

    useEffect(() => {
        if (!loading && requestLock) {
            setVisible(true)
            setLoading(true)
            exportFunction(exportCallback)
                .then(() => {
                    setInitStatus('success')
                    setCollectStatus('success')
                    setBuildStatus('success')
                    setStoreStatus('success')
                    setIsLoading(false)
                })
                .catch((err) => {
                    if (collectStatus === 'active') {
                        setCollectStatus('exception')
                    } else if (collectStatus === 'success' && buildStatus === 'active') {
                        setBuildStatus('exception')
                    }
                    setVisible(false)
                    setCollectProgress(0)
                    setCollectStatus('active')
                    setBuildProgress(0)
                    setBuildStatus('normal')
                    setIsDone(false)
                    onFail(err)
                })
                .finally(() => {
                    setIsDone(true)
                })
        }
    }, [
        requestLock,
        exportFunction,
        setIsLoading,
        setLoading,
        buildStatus,
        collectStatus,
        loading,
        onFail,
    ])

    function getText() {
        let text = 'Setting up lock'
        if (initstoreStatus === 'success') {
            text = 'Collecting Data'
        }
        if (collectStatus === 'success') {
            text = 'Compiling Excel File'
        }
        if (buildStatus === 'success') {
            text = 'Storing locked report'
        }
        if (storeStatus === 'success') {
            text = ''
        }
        if (overallStatus === 'active') {
            if (overallProgress < 10) {
                text = 'Collecting data'
            } else if (overallProgress < 30) {
                text = 'Compiling Excel file'
            } else if (overallProgress < 40) {
                text = 'Indexing files'
            } else if (overallProgress < 100) {
                text = 'Compressing documents'
            } else {
                text = 'Completed!'
            }
        }
        return <h4>{text}</h4>
    }

    function getCompletionPercentage() {
        if (overallStatus === 'normal') {
            return (initProgress + collectProgress + buildProgress + storeProgress) / 4
        } else {
            return overallProgress
        }
    }

    return (
        <Modal
            visible={visible}
            footer={
                <Button disabled={!isDone} icon={!isDone && <SyncOutlined spin />} onClick={close}>
                    Return
                </Button>
            }
        >
            <div>
                {getText()}
                <Progress
                    percent={getCompletionPercentage()}
                    status={storeStatus}
                    format={(p) => `${Math.round(p)} %`}
                />
                <h3>Please stay on this site until the process has finished!</h3>
                {isDone && <h3>Your compliance report has locked successfully</h3>}
            </div>
        </Modal>
    )
}
